import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Rocket, Plus, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, uploadImages } from "@/lib/api";
import { buildRows, rowPayload, validateRow } from "@/lib/bulk";
import { useMyCardBacks, useMySets } from "@/lib/queries";
import { PageHeader, Pager } from "@/components/common/PageParts";
import { ExampleDownloads } from "@/components/import/ExampleDownloads";
import { BulkSelectStep } from "@/components/upload/BulkSelectStep";
import { BulkToolbar } from "@/components/upload/BulkToolbar";
import { BulkReviewGrid } from "@/components/upload/BulkReviewGrid";
import { FaceDetailsDialog } from "@/components/upload/FaceDetailsDialog";

const PAGE_SIZE = 25;
const CHUNK = 100;

export default function BulkUpload() {
  const qc = useQueryClient();
  const { data: sets = [] } = useMySets();
  const { data: backs = [] } = useMyCardBacks();
  const [files, setFiles] = useState([]);
  const [metaFile, setMetaFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [pool, setPool] = useState([]);
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [detailKey, setDetailKey] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [rights, setRights] = useState(false);

  const uploadAll = async (list) => {
    setProgress({ done: 0, total: list.length });
    const results = await uploadImages(list, "card_image", (done, total) => setProgress({ done, total }));
    const failed = results.filter((r) => !r.ok);
    failed.forEach((f) => toast.error(`${f.filename}: ${f.error}`));
    return results.filter((r) => r.ok);
  };

  const start = async () => {
    try {
      let metaCards = [];
      if (metaFile) {
        const form = new FormData();
        form.append("file", metaFile);
        const { data } = await api.post("/uploads/metadata", form);
        metaCards = data.cards;
        data.warnings.slice(0, 5).forEach((w) => toast.warning(w));
      }
      const uploaded = await uploadAll(files);
      setPool(uploaded);
      setRows(buildRows(uploaded, metaCards));
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  const addMore = async (list) => {
    try {
      const uploaded = await uploadAll(list);
      setPool((p) => [...p, ...uploaded]);
      setRows((r) => [...r, ...buildRows(uploaded)]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  const update = (key) => (patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch, status: r.status === "published" ? r.status : "ready", error: "" } : r)));
  const remove = (key) => setRows((rs) => rs.filter((r) => r.key !== key));
  const applyAll = (patch) => setRows((rs) => rs.map((r) => (r.status === "published" ? r : { ...r, ...patch })));

  const saveDetails = (draft) => {
    const backId = draft.faces[1]?.image_file_id;
    // A face chosen as a back image should not also be published as its own card.
    setRows((rs) => rs.filter((r) => r.key === draft.key || !backId || r.faces[0].image_file_id !== backId || r.status === "published")
      .map((r) => (r.key === draft.key ? { ...draft, status: "ready", error: "" } : r)));
    setDetailKey(null);
  };

  const publish = async () => {
    const pending = rows.filter((r) => r.status !== "published");
    const checked = pending.map((r) => ({ key: r.key, error: validateRow(r, rights) }));
    const invalid = new Map(checked.filter((c) => c.error).map((c) => [c.key, c.error]));
    setRows((rs) => rs.map((r) => (invalid.has(r.key) ? { ...r, status: "error", error: invalid.get(r.key) } : r)));
    const ready = pending.filter((r) => !invalid.has(r.key));
    if (!ready.length) return toast.error("Fix the highlighted rows before publishing");
    setPublishing(true);
    let created = 0;
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const batch = ready.slice(i, i + CHUNK);
        const { data } = await api.post("/cards/bulk", { cards: batch.map((r) => rowPayload(r, rights)) });
        const ok = new Map(data.created.map((c) => [batch[c.index].key, c.card_id]));
        const bad = new Map(data.errors.map((e) => [batch[e.index].key, typeof e.error === "string" ? e.error : "Invalid card"]));
        created += ok.size;
        setRows((rs) => rs.map((r) => (ok.has(r.key) ? { ...r, status: "published", card_id: ok.get(r.key), error: "" } : bad.has(r.key) ? { ...r, status: "error", error: bad.get(r.key) } : r)));
      }
      toast.success(`${created} card${created === 1 ? "" : "s"} published`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPublishing(false);
    }
  };

  const counts = rows ? { total: rows.length, published: rows.filter((r) => r.status === "published").length, errors: rows.filter((r) => r.status === "error").length } : null;

  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Bulk upload" title={rows ? "Review your cards" : "Upload a custom set"} testid="bulk-header"
        description={rows ? "Correct any imported details, then publish. Nothing is public until you press Publish." : "Upload hundreds of card images at once, optionally with a CSV or JSON metadata file. You'll review every card before publishing."}
        actions={rows && (
          <>
            <label className="btn-outline cursor-pointer" data-testid="bulk-add-more-label">
              {progress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add images
              <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="bulk-add-more-input"
                onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ""; list.length && addMore(list); }} />
            </label>
            {counts.published > 0 && (
              <button className="btn-ghost" onClick={() => setRows((rs) => rs.filter((r) => r.status !== "published"))} data-testid="bulk-clear-published"><Eraser className="h-4 w-4" />Clear published</button>
            )}
            <button className="btn-gold" onClick={publish} disabled={publishing || counts.published === counts.total} data-testid="bulk-publish-button">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}Publish {counts.total - counts.published} card{counts.total - counts.published === 1 ? "" : "s"}
            </button>
          </>
        )} />
      {!rows ? (
        <>
          <BulkSelectStep files={files} setFiles={setFiles} metaFile={metaFile} setMetaFile={setMetaFile} onStart={start} progress={progress} />
          <div className="mt-8"><ExampleDownloads testid="bulk-examples" showImporterLink /></div>
        </>
      ) : (
        <>
          <BulkToolbar sets={sets} backs={backs} applyAll={applyAll} rights={rights} setRights={setRights} />
          <p className="mb-3 font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="bulk-counts">
            {counts.total} rows · {counts.published} published · <span className={counts.errors ? "text-red-300" : ""}>{counts.errors} need attention</span>
            {counts.published > 0 && <> · <Link to="/dashboard" className="text-amber-300 hover:underline" data-testid="bulk-dashboard-link">View in My Workshop</Link></>}
          </p>
          <BulkReviewGrid rows={rows} page={page} pageSize={PAGE_SIZE} update={update} remove={remove} openDetails={setDetailKey} />
          <Pager page={page} pageSize={PAGE_SIZE} total={rows.length} onChange={setPage} testid="bulk-pager" />
          <FaceDetailsDialog row={rows.find((r) => r.key === detailKey) || null} pool={pool} onClose={() => setDetailKey(null)} onSave={saveDetails} />
        </>
      )}
    </div>
  );
}
